import { ApiAppointment } from "../../core/apis";
import { AppRouteUi } from "../../config/appRoute";
import { Button } from "../../components/button.component";
import { Form, Loader, Toggle } from "rsuite";
import { useHistory } from "react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { addMonths } from "date-fns";
import {
  DateInput,
  NumberInput,
  SelectPickerInput,
  TagInputWithIcon,
} from "../../components/textInput.component";
import { UploadedFileType } from "../../models/forms/uploadFileType";
import {
  DoesObjectHasAnyInvalidValue,
  generateMultipartFormData,
} from "../../utils/formUtils";
import { AppointmentFormSchema } from "../../models/forms/appointmentForm";
import { ApiPatient } from "../../core/apis/patient";
import { ApiCaregiver } from "../../core/apis/caregiver";
import { UtilityDate } from "../../utils/dateUtils";
import { StorageAuth } from "../../core/localStorage/StorageAuth";
import { ActionsAppointmentTable } from "../../core/redux/slices/sliceAppointmentTable";
import { useAppDispatch } from "../../core/redux/ReduxStore";
import { ApiSchemaPatient } from "../../models/api/patient";
import FormHelpText from "rsuite/esm/FormHelpText";
import { SeeTasksCell } from "../../components/tableItem.component";
import { Link, useLocation } from "react-router-dom";
import { ApiSchemaCaregiver } from "../../models/api/caregiver";
import { addHours, differenceInDays } from "date-fns/esm";
import { DHS_INSURANCE_NAME } from "../../config/constants";
import { useOverlappedAppointment } from "../../hooks";

type AppointmentFormData = {
  AppointmentStartDate: Date;
  AppointmentEndDate: Date;
  TaskStartTime: Date;
  MaxHours: string;
  PatientSKey: string;
  ACStartDate?: string;
  ACEndDate?: string;
  RequestedCaregiverSKeys: string[];
  Certificates: UploadedFileType[];
};

export const AddAppointment = ({
  setTitle,
}: {
  setTitle: (title: string) => void;
  showAvailableDays?: boolean;
}) => {
  const routeHistory = useHistory<{ from?: string }>();
  const { data: patients, isValidating: isPatientValidating } =
    ApiPatient.GetAllFromCompany.Hook();
  const { data: nurses, isValidating: isCaregiverValidating } =
    ApiCaregiver.GetAllFromCompany.Hook();

  const { IsAutoClockInEnable } = StorageAuth;
  const [formData, setFormData] = useState<AppointmentFormData>({
    AppointmentStartDate: new Date(),
    AppointmentEndDate: addMonths(new Date(), 6),
    TaskStartTime: addHours(new Date(), 1),
    MaxHours: "",
    PatientSKey: "",
    RequestedCaregiverSKeys: [],
    Certificates: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState({});
  const [isDisabled, setIsDisabled] = useState(true);
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [autoClockCheckIn, setAutoClockCheckIn] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<ApiSchemaPatient>();
  const [selectedNurse, setSelectedNurse] = useState<ApiSchemaCaregiver>();
  const containerRef = useRef<any>(null);
  const haveMonthlyTask = selectedPatient?.taskDetailsList.some(
    (task) => task.isMonthly
  );
  const {
    haveIssue,
    isFetchingAppointment,
    overlappedAppointment,
    existingAppointment,
    setOverlappedAppointment,
  } = useOverlappedAppointment({
    startDate: formData.AppointmentStartDate,
    endDate: formData.AppointmentEndDate,
    patientSKey: selectedPatient?.sKey,
  });
  const { state } = useLocation<{
    patientSKey?: string;
    caregiverSKey?: string;
  }>();

  useEffect(() => {
    if (state?.patientSKey) {
      const patient = patients?.find(
        (patient) => patient.sKey === state.patientSKey
      );
      setSelectedPatient(patient);
      setFormData((prev) => ({
        ...prev,
        PatientSKey: patient?.sKey ?? "",
      }));
      setTitle("Patient: " + patient?.firstName + " " + patient?.lastName);
    }
  }, [state, patients, setTitle]);

  useEffect(() => {
    if (state?.caregiverSKey) {
      const caregiver = nurses?.find(
        (caregiver) => caregiver.sKey === state.caregiverSKey
      );
      setSelectedNurse(caregiver);
      setFormData((prev) => ({
        ...prev,
        RequestedCaregiverSKeys: caregiver?.sKey ? [caregiver.sKey] : [],
      }));
      setTitle(
        "Caregiver: " + caregiver?.firstName + " " + caregiver?.lastName
      );
    }
  }, [state, nurses, setTitle]);

  const dispatch = useAppDispatch();

  const patientsSelectItems = useMemo(() => {
    let filteredData = patients
      ? !selectedNurse
        ? patients
        : patients.filter((patient) =>
            patient?.languages?.some((language) =>
              selectedNurse?.languages?.includes(language)
            )
          )
      : [];

    if (selectedNurse) {
      filteredData = filteredData.filter((patient) => {
        let patientWeeklyAvailability: string[] = [];
        patient.taskDetailsList?.forEach((task) => {
          task.weeklyAvailabilities?.forEach((day) => {
            if (!patientWeeklyAvailability.includes(day)) {
              patientWeeklyAvailability.push(day);
            }
          });
        });
        return patientWeeklyAvailability.some((day) =>
          selectedNurse.weeklyAvailabilities?.includes(day)
        );
      });
    }

    return filteredData.map((patient) => ({
      label: `${patient.firstName} ${patient.lastName} ${
        patient.isDeActivated! ? "[Deactivated]" : ""
      }`,
      value: patient.sKey,
    }));
  }, [patients, selectedNurse]);

  const nursesSelectItems = useMemo(() => {
    const haveAllMonthlyTask = selectedPatient?.taskDetailsList.every(
      (task) => task.isMonthly
    );
    let filteredData = nurses
      ? !selectedPatient
        ? nurses
        : nurses.filter((nurse) =>
            nurse.languages.some((language) =>
              selectedPatient?.languages?.includes(language)
            )
          )
      : [];
    if (
      !haveAllMonthlyTask &&
      selectedPatient &&
      selectedPatient.insuranceTypeName !== DHS_INSURANCE_NAME
    ) {
      let patientWeeklyAvailability: string[] = [];
      selectedPatient?.taskDetailsList?.forEach((task) => {
        task.weeklyAvailabilities?.forEach((day) => {
          if (!patientWeeklyAvailability.includes(day)) {
            patientWeeklyAvailability.push(day);
          }
        });
      });
      filteredData = filteredData.filter((nurse) => {
        return patientWeeklyAvailability.some((day) =>
          nurse.weeklyAvailabilities?.includes(day)
        );
      });
    }
    return filteredData.map((nurse) => ({
      label:
        nurse.firstName +
        " " +
        nurse.lastName +
        ` (${
          nurse.languages.length ? nurse.languages.join(", ") : "No Language"
        }) / (Transport ${nurse.isTransportAvailable ? "" : "Not"} Available)`,
      value: nurse.sKey,
    }));
  }, [nurses, selectedPatient]);

  const notWithinServiceAuthorizationPeriod = useMemo(
    () =>
      !!formData.AppointmentEndDate &&
      !!selectedPatient?.serviceAuthorizationEndDate &&
      new Date(
        formData.AppointmentEndDate.getFullYear(),
        formData.AppointmentEndDate.getMonth(),
        formData.AppointmentEndDate.getDate()
      ) > new Date(selectedPatient.serviceAuthorizationEndDate),
    [formData.AppointmentEndDate, selectedPatient]
  );

  const isSelectedNurseDeactivated = () => {
    return formData.RequestedCaregiverSKeys?.some((cgSKey) => {
      return nurses
        ?.filter((nurse) => nurse.sKey === cgSKey)
        .some((nurse) => nurse.isDeActivated);
    });
  };

  useEffect(() => {
    const propertyToCheck = { ...formData };
    const isEmpty = Object.values({ propertyToCheck }).some(
      DoesObjectHasAnyInvalidValue
    );
    const errorCount = Object.keys(formError).length;
    const isDisabled =
      isEmpty ||
      !!errorCount ||
      isBackdated() ||
      (autoClockCheckIn &&
        (formData.RequestedCaregiverSKeys.length > 1 ||
          !startDate ||
          !endDate)) ||
      formData.RequestedCaregiverSKeys.length === 0 ||
      notWithinServiceAuthorizationPeriod ||
      !!selectedPatient?.isDeActivated ||
      !!selectedNurse?.isDeActivated ||
      isSelectedNurseDeactivated! ||
      !selectedPatient?.taskDetailsList?.length;

    setIsDisabled(isDisabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoClockCheckIn,
    endDate,
    formData,
    formError,
    startDate,
    notWithinServiceAuthorizationPeriod,
    selectedPatient?.isDeActivated,
    selectedNurse?.isDeActivated,
    selectedPatient?.taskDetailsList,
  ]);

  const handleChange = useCallback(
    (data: any, _: any) =>
      setFormData((prevFormData) => ({ ...prevFormData, ...data })),
    []
  );

  useEffect(() => {
    formData.AppointmentStartDate > formData.AppointmentEndDate &&
      setFormData((prev) => ({
        ...prev,
        AppointmentEndDate: formData.AppointmentStartDate,
      }));
    startDate! > endDate! && setEndDate(startDate);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.AppointmentStartDate, startDate]);

  useEffect(() => {
    setSelectedPatient(
      formData.PatientSKey
        ? patients?.filter(
            (patient) => patient.sKey === formData.PatientSKey
          )[0]
        : undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.PatientSKey, patients]);

  const { CompanyId } = StorageAuth;

  const handleSubmit = async () => {
    if (isDisabled) return;
    setIsLoading(true);
    const multipartFormData = generateMultipartFormData(formData);

    if (autoClockCheckIn) {
      multipartFormData.append("AutoClockCheckIn", "true");
      startDate &&
        multipartFormData.append("StartDate", startDate?.toISOString());
      endDate && multipartFormData.append("EndDate", endDate?.toISOString());
    } else multipartFormData.append("AutoClockCheckIn", "false");

    multipartFormData.delete("TaskStartTime");
    const startTime = UtilityDate.combineDateTime(
      formData.AppointmentStartDate,
      formData.TaskStartTime,
      {
        hideSecond: true,
        hideMillisecond: true,
      }
    );
    multipartFormData.append(
      "TaskStartTime",
      UtilityDate.ToIsoUtcDateTime(startTime)
    );

    CompanyId && multipartFormData.append("CompanyId", CompanyId);

    const result = await ApiAppointment.Add(multipartFormData);

    if (result.isSuccess) {
      dispatch(ActionsAppointmentTable.goToPage(1));
      routeHistory.length > 1
        ? routeHistory.goBack()
        : routeHistory.push(AppRouteUi.Root);
    }
    setIsLoading(false);
  };

  const handleTaskStartTimeChange = (time: Date) => {
    setFormData((prev) => ({ ...prev, TaskStartTime: time }));
  };

  // const appointmentWillNotWorkToday = () => {
  //   if (formData.AppointmentStartDate && formData.TaskStartTime) {
  //     return formData.AppointmentStartDate.getMonth() <=
  //       new Date().getMonth() &&
  //       formData.AppointmentStartDate.getDate() === new Date().getDate() &&
  //       formData.TaskStartTime.getHours() < new Date().getHours()
  //       ? true
  //       : formData.AppointmentStartDate.getMonth() <= new Date().getMonth() &&
  //         formData.AppointmentStartDate.getDate() === new Date().getDate() &&
  //         formData.TaskStartTime.getHours() === new Date().getHours() &&
  //         formData.TaskStartTime.getMinutes() < new Date().getMinutes()
  //       ? true
  //       : false;
  //   } else return false;
  // };

  useEffect(() => {
    setStartDate((prev) =>
      !prev ||
      prev < formData.AppointmentStartDate ||
      prev > formData.AppointmentEndDate
        ? formData.AppointmentStartDate
        : prev
    );
    setEndDate((prev) =>
      !prev ||
      prev > formData.AppointmentEndDate ||
      prev < formData.AppointmentStartDate
        ? formData.AppointmentEndDate
        : prev
    );
  }, [formData.AppointmentStartDate, formData.AppointmentEndDate]);

  useEffect(() => {
    const totalHours = (selectedPatient?.totalUnit ?? 0) / 4;
    let totalDays =
      differenceInDays(
        formData.AppointmentEndDate,
        formData.AppointmentStartDate
      ) + 1;
    totalDays = 30;
    const totalHoursPerDay = Math.min(16, totalHours / totalDays).toFixed(2);

    totalHoursPerDay &&
      setFormData((prev) => ({
        ...prev,
        MaxHours: isNaN(parseFloat(totalHoursPerDay))
          ? ""
          : `${totalHoursPerDay}`,
      }));
  }, [
    formData.AppointmentEndDate,
    formData.AppointmentStartDate,
    selectedPatient?.totalUnit,
  ]);

  const isBackdated = () => {
    if (!formData.AppointmentStartDate || !formData.TaskStartTime) {
      return;
    }
    const appointmentStartTime = new Date(
      formData.AppointmentStartDate.getFullYear(),
      formData.AppointmentStartDate.getMonth(),
      formData.AppointmentStartDate.getDate(),
      formData.TaskStartTime.getHours(),
      formData.TaskStartTime.getMinutes(),
      formData.TaskStartTime.getSeconds()
    );
    const currentTime = new Date();
    if (
      currentTime > appointmentStartTime &&
      formData.RequestedCaregiverSKeys.length > 1
    )
      return true;
    return false;
  };

  const caregiverValidationErrorMessage = () => {
    if (autoClockCheckIn && formData.RequestedCaregiverSKeys.length > 1)
      return "Please select only one caregiver if auto clock is on";
    if (isBackdated())
      return "Please select only one caregiver if Appointment Start Date is backdated.";
    return "";
  };

  return (
    <Form
      className="row"
      onCheck={setFormError}
      model={AppointmentFormSchema}
      onChange={handleChange}
      onSubmit={handleSubmit}
      formValue={formData}
      fluid
    >
      <div
        className="col-md-10 col-12 border-end px-4 add-page-ctl"
        ref={containerRef}
      >
        {isPatientValidating || isCaregiverValidating ? (
          <div className="py-4">
            <Loader center title="Loading" />
          </div>
        ) : !patients?.length || !nurses?.length ? (
          <div className="py-4 text-center m-auto">
            <p className="mb-2">
              There's no {!patients?.length ? "patients" : "caregivers"}. Please
              create a {!patients?.length ? "patient" : "caregiver"} first.
            </p>
            <Button
              name={"Add New " + (!patients?.length ? "patient" : "caregiver")}
              className="me-2 btn-green"
              onClick={() =>
                routeHistory.push(
                  AppRouteUi[!patients?.length ? "Patient" : "Caregiver"].Add(),
                  {
                    from: AppRouteUi.Appointment.Add(),
                  }
                )
              }
            />
          </div>
        ) : (
          <div>
            {/* {appointmentWillNotWorkToday() && (
              <div className="alert alert-danger" role="alert">
                This Appointment won't work Today
              </div>
            )} */}

            {notWithinServiceAuthorizationPeriod && (
              <div className="alert alert-danger" role="alert">
                Appointment should end within the service authorization period
                of the patient (
                {new Date(
                  selectedPatient?.serviceAuthorizationEndDate || ""
                ).toLocaleDateString()}
                ). &nbsp;
                <Link
                  to={{
                    pathname: AppRouteUi.Patient.UpdateById(
                      selectedPatient?.sKey!
                    ),
                    state: { from: "appointment-create" },
                  }}
                  className="ml-2"
                >
                  Edit Patient
                </Link>
              </div>
            )}
            {overlappedAppointment.exist && (
              <div className="alert alert-danger" role="alert">
                Patient have already appointment from{" "}
                {overlappedAppointment.from} to {overlappedAppointment.to}.
                Please{" "}
                <Link
                  to={{
                    pathname: AppRouteUi.Appointment.UpdateById(
                      overlappedAppointment.sKey
                    ),
                    state: { from: "appointment-create" },
                  }}
                  className="ml-2"
                >
                  update
                </Link>{" "}
                that appointment or change the current appointment schedule.
              </div>
            )}
            {!!selectedPatient?.isDeActivated && (
              <div className="alert alert-danger" role="alert">
                Your selected patient is deactivated. You can not create an
                appointment for a deactivated patient.
              </div>
            )}
            {selectedPatient && !selectedPatient?.taskDetailsList?.length && (
              <div className="alert alert-danger" role="alert">
                Your selected patient have no task. You can not create an
                appointment for this patient.
              </div>
            )}
            {(!!selectedNurse?.isDeActivated ||
              isSelectedNurseDeactivated()) && (
              <div className="alert alert-danger" role="alert">
                Your selected caregiver is deactivated. You can not create an
                appointment for a deactivated caregiver.
              </div>
            )}
            <div className="row">
              <DateInput
                name="AppointmentStartDate"
                disabledDate={(date?: Date) =>
                  !!date &&
                  !!formData.AppointmentEndDate &&
                  date > formData.AppointmentEndDate
                }
                required
              >
                Appointment Start Date
              </DateInput>

              <DateInput
                name="AppointmentEndDate"
                disabledDate={(date?: Date) =>
                  !!date &&
                  !!formData.AppointmentStartDate &&
                  date < formData.AppointmentStartDate
                }
                required
              >
                Appointment End Date
              </DateInput>
            </div>

            <div className="row">
              <SelectPickerInput
                data={patientsSelectItems}
                disabled={!!selectedPatient && !!state?.patientSKey}
                placeholder="Select Patient"
                name="PatientSKey"
                fullWidth
                required
                renderEmpty={<p className="text-muted p-3">No Valid Patient</p>}
                onChange={() => {
                  !selectedNurse &&
                    setFormData((prev) => ({
                      ...prev,
                      RequestedCaregiverSKeys: [],
                    }));
                  setOverlappedAppointment({
                    ...overlappedAppointment,
                    exist: false,
                  });
                }}
              >
                Add Patient
              </SelectPickerInput>
            </div>

            {isFetchingAppointment && (
              <div className="text-center">
                <Loader />
              </div>
            )}
            {existingAppointment && (
              <FormHelpText>
                <div className="row mb-2 mt-1">
                  <div className="col-md-7 col-12">
                    <b>Language(s):</b>{" "}
                    {selectedPatient?.languages?.length
                      ? selectedPatient?.languages?.join(", ")
                      : "No Languages"}
                  </div>
                  <div className="col-md-3 col-12">
                    <b>Transport:</b>{" "}
                    {selectedPatient?.isTransportAvailable
                      ? "Available"
                      : "Not Available"}
                  </div>
                  <div className="col-md-2 col-12">
                    <SeeTasksCell
                      taskDetailsList={selectedPatient?.taskDetailsList}
                    />
                  </div>
                </div>
              </FormHelpText>
            )}

            {(!!selectedPatient || !!selectedNurse) && (
              <div className="row my-2">
                <TagInputWithIcon
                  placeholder="Search Caregivers"
                  required
                  disabled={!!selectedNurse}
                  name="RequestedCaregiverSKeys"
                  data={nursesSelectItems}
                  renderEmpty={
                    <p className="text-muted p-3">No Valid Caregivers</p>
                  }
                  errorMessage={
                    // autoClockCheckIn &&
                    // formData.RequestedCaregiverSKeys.length > 1 &&
                    // "Please select only one caregiver if auto clock is on"
                    caregiverValidationErrorMessage()
                  }
                  fullWidth
                >
                  Add Caregivers
                </TagInputWithIcon>
              </div>
            )}

            <div className="row mt-2">
              <DateInput
                name="TaskStartTime"
                timeChange={handleTaskStartTimeChange}
                time
                required
                cleanable={false}
              >
                Task start time
              </DateInput>
              <NumberInput name="MaxHours" min={0} max={16} disabled required>
                Max hours
              </NumberInput>
            </div>

            {!!IsAutoClockInEnable && (
              <div className="align-items-center m-1 my-3">
                Enable auto clock in{" "}
                <Toggle
                  onChange={() => setAutoClockCheckIn(!autoClockCheckIn)}
                  checked={autoClockCheckIn}
                  size="sm"
                  className="ms-2"
                />
                {haveMonthlyTask && autoClockCheckIn && (
                  <small className="d-inline-block m-2 text-muted">
                    ( Patient have monthly task. Auto clock dates have disabled.
                    )
                  </small>
                )}
              </div>
            )}

            {autoClockCheckIn && (
              <div className="row">
                <DateInput
                  name="ACStartDate"
                  disabled
                  disabledDate={(date) =>
                    !!date &&
                    ((!!formData.AppointmentStartDate &&
                      date < formData.AppointmentStartDate) ||
                      (!!endDate && date > endDate))
                  }
                  onChange={setStartDate}
                  value={startDate}
                  required
                >
                  Start Date
                </DateInput>
                <DateInput
                  name="ACEndDate"
                  disabled
                  disabledDate={(date) =>
                    !!date &&
                    ((!!formData.AppointmentEndDate &&
                      date > formData.AppointmentEndDate) ||
                      (!!startDate && date < startDate))
                  }
                  onChange={setEndDate}
                  value={endDate}
                  required
                >
                  End Date
                </DateInput>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="col-md-2 col-12">
        <Button
          className="m-1 w-100 btn-green"
          type="submit"
          name="Save"
          loading={isLoading}
          disabled={isDisabled || haveIssue}
        />
        <Button
          onClick={() => {
            routeHistory.length > 1
              ? routeHistory.goBack()
              : routeHistory.push(AppRouteUi.Root);
          }}
          type="button"
          className="m-1 w-100 btn-green-subtle"
          disabled={isLoading}
          name="Return without saving"
        />
      </div>
    </Form>
  );
};
